"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useRouter } from "next/navigation";
import {
  COMMAND_PALETTE_ITEMS,
  filterCommandPaletteItems,
  type CommandPaletteItem,
} from "@/lib/navigation/site-surfaces";

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return target.closest('[contenteditable="true"]') != null;
}

export function CommandPalette() {
  const router = useRouter();
  const titleId = useId();
  const inputId = useId();
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => filterCommandPaletteItems(COMMAND_PALETTE_ITEMS, query),
    [query],
  );

  useEffect(() => {
    setSelectedIndex((i) =>
      filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1),
    );
  }, [filtered.length]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const goTo = useCallback(
    (item: CommandPaletteItem) => {
      router.push(item.href);
      close();
    },
    [router, close],
  );

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedIndex(0);
      return;
    }
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isModK =
        (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (!isModK) return;
      if (!open && isEditableTarget(e.target)) return;
      e.preventDefault();
      setOpen((was) => !was);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, close]);

  const onPanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % filtered.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[selectedIndex];
      if (item) goTo(item);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[400] flex items-start justify-center bg-black/40 p-4 pt-[max(4rem,12vh)] backdrop-blur-[2px] dark:bg-black/60"
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-lg overflow-hidden rounded-xl border border-black/10 bg-tan shadow-[0_24px_80px_rgba(0,0,0,0.2)] dark:border-white/12 dark:bg-background-dark dark:shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
        onKeyDown={onPanelKeyDown}
      >
        <div className="border-b border-black/10 px-4 py-3 dark:border-white/10">
          <p id={titleId} className="sr-only">
            Go to page
          </p>
          <label htmlFor={inputId} className="sr-only">
            Search pages
          </label>
          <input
            ref={inputRef}
            id={inputId}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Search pages…"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-controls={listboxId}
            aria-activedescendant={
              filtered[selectedIndex]
                ? `cmd-row-${filtered[selectedIndex].id}`
                : undefined
            }
            className="w-full border-0 bg-transparent text-base text-text-light outline-none placeholder:text-text-muted-light dark:text-text-dark dark:placeholder:text-text-muted-dark"
          />
        </div>

        <div
          id={listboxId}
          role="listbox"
          aria-label="Pages"
          className="max-h-[min(50vh,22rem)] overflow-y-auto py-2"
        >
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-text-muted-light dark:text-text-muted-dark">
              No matches. Try another word.
            </p>
          ) : (
            filtered.map((item, i) => {
              const showGroup =
                i === 0 || item.group !== filtered[i - 1]!.group;
              const selected = i === selectedIndex;
              return (
                <Fragment key={item.id}>
                  {showGroup ? (
                    <div
                      role="presentation"
                      className={`px-4 pb-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-text-muted-light dark:text-text-muted-dark ${i === 0 ? "pt-0" : "pt-2"}`}
                    >
                      {item.group}
                    </div>
                  ) : null}
                  <div role="presentation" className="px-2">
                    <button
                      type="button"
                      role="option"
                      id={`cmd-row-${item.id}`}
                      aria-selected={selected}
                      onMouseEnter={() => setSelectedIndex(i)}
                      onClick={() => goTo(item)}
                      className={`flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                        selected
                          ? "bg-black/[0.06] text-text-light dark:bg-white/10 dark:text-text-dark"
                          : "text-text-light hover:bg-black/[0.04] dark:text-text-dark dark:hover:bg-white/[0.06]"
                      }`}
                    >
                      <span className="font-medium">{item.label}</span>
                      <span className="ml-3 shrink-0 font-mono text-[11px] text-text-muted-light dark:text-text-muted-dark">
                        {item.href}
                      </span>
                    </button>
                  </div>
                </Fragment>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-black/10 px-4 py-2.5 text-[11px] text-text-muted-light dark:border-white/10 dark:text-text-muted-dark">
          <span>
            <kbd className="rounded border border-current/20 px-1 py-0.5 font-mono">
              ↑
            </kbd>{" "}
            <kbd className="rounded border border-current/20 px-1 py-0.5 font-mono">
              ↓
            </kbd>{" "}
            to move ·{" "}
            <kbd className="rounded border border-current/20 px-1 py-0.5 font-mono">
              Enter
            </kbd>{" "}
            to open
          </span>
          <span>
            <kbd className="rounded border border-current/20 px-1 py-0.5 font-mono">
              esc
            </kbd>{" "}
            close
          </span>
        </div>
      </div>
    </div>
  );
}
